// App frame per the signed-off mockups: 232px sidebar on --bg, main content
// as an inset rounded panel. Sidebar counts are ACTIONABLE items (leads =
// follow-ups due, tasks = due today + overdue, finance = overdue invoices),
// never totals — gold when > 0. Projects shows the active count, plain.

import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
  type SVGProps,
} from 'react';
import { NavLink, useLocation, useNavigationType } from 'react-router-dom';
import { clearNavScroll, getSessionStorage, readNav, writeNavScroll } from '../lib/viewState';
import { api } from '../lib/api';
import { shouldRestoreScroll } from '../lib/scrollMemory';
import { QuickAddProvider } from '../lib/quickAdd';
import { mergeSidebarLayout, useSidebarLayout } from '../lib/sidebarLayout';
import {
  DEFAULT_SIDEBAR_LAYOUT,
  SIDEBAR_GROUP_LABELS,
  SIDEBAR_NAV_ITEMS,
  type SidebarNavItem,
} from '../lib/sidebarNav';
import { SIDEBAR_GROUPS } from '../../shared/prefs';
import { applyTheme, currentTheme, nextTheme, type Theme } from '../lib/theme';
import { useIsMobile } from '../lib/useIsMobile';
import CommandPalette from './CommandPalette';
import MoreSheet from './MoreSheet';
import QuickAddSheet from './QuickAddSheet';
import TabBar from './TabBar';
import {
  AutomationsIcon,
  MoonIcon,
  OverviewIcon,
  PlusIcon,
  SettingsIcon,
  SunIcon,
} from './icons';

export interface Badges {
  leads: number;
  projects: number;
  tasks: number;
  finance: number;
  outreach: number;
  drives?: number;
}

interface ThemeState {
  preference: Theme;
  effective: 'dark' | 'light';
}

function readThemeState(): ThemeState {
  const preference = currentTheme();
  const systemPrefersLight = window.matchMedia('(prefers-color-scheme: light)').matches;
  const effective = preference === 'system'
    ? (systemPrefersLight ? 'light' : 'dark')
    : preference;
  return { preference, effective };
}

/** Routes whose own primary action replaces the quick-add "+" (§0.4.4), plus
    the three pages that have nothing to add. */
function hidesFab(pathname: string): boolean {
  if (pathname.startsWith('/tools/') || pathname === '/meetings' || pathname.startsWith('/meetings/')) return true;
  if (pathname === '/marketing' || pathname.startsWith('/marketing/')) return true;
  if (pathname === '/settings' || pathname === '/automations' || pathname === '/stats' || pathname === '/drives') return true;
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 2 && ['leads', 'projects', 'clients', 'dashboards'].includes(segments[0])) {
    return true;
  }
  return segments.length === 3 && segments[0] === 'finance' && segments[1] === 'invoices';
}

function NavItem({
  to,
  label,
  icon: Icon,
  count,
  alert,
  tool,
}: {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  count?: number;
  alert?: boolean;
  tool?: boolean;
}) {
  const { pathname } = useLocation();
  return (
    <NavLink to={to} replace={to === pathname} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
      <Icon className="nav-icon" />
      <span className="nav-label">{label}</span>
      {count !== undefined && count > 0 && (
        <span className={`nav-count${alert ? ' alert' : ''}`}>{count}</span>
      )}
      {tool && <span className="nav-ext">↗</span>}
    </NavLink>
  );
}

export default function Shell({ children }: { children: ReactNode }) {
  const [badges, setBadges] = useState<Badges | null>(null);
  const badgeRequest = useRef(0);
  const [palette, setPalette] = useState<null | 'search' | 'task'>(null);
  const [theme, setTheme] = useState<ThemeState>(readThemeState);
  // ≤880px: the bottom bar replaces the sidebar, More/quick-add replace the
  // drawer (mobile overhaul Phase 0 §0.4).
  const [moreOpen, setMoreOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [keyboardUp, setKeyboardUp] = useState(false);
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const currentPath = useRef(pathname);
  const previousRestorePath = useRef<string | null>(null);
  useLayoutEffect(() => {
    currentPath.current = pathname;
  }, [pathname]);
  const sidebarPref = useSidebarLayout();
  const sidebarLayout = useMemo(
    () => mergeSidebarLayout(sidebarPref, DEFAULT_SIDEBAR_LAYOUT),
    [sidebarPref],
  );

  const renderSidebarItems = (ids: string[]) =>
    ids.map((id) => {
      const item: SidebarNavItem = SIDEBAR_NAV_ITEMS[id];
      return (
        <NavItem
          key={item.to}
          to={item.to}
          label={item.label}
          icon={item.icon}
          count={item.badge ? badges?.[item.badge] : undefined}
          alert={item.alert}
          tool={item.tool}
        />
      );
    });

  const renderSidebarGroup = (label: string, ids: string[]) =>
    ids.length === 0 ? null : (
      <>
        <div className="nav-section">{label}</div>
        {renderSidebarItems(ids)}
      </>
    );

  const loadBadges = useCallback(() => {
    const generation = ++badgeRequest.current;
    // Keep each source independent: a drive query failure must not hide the
    // other actionable counts. Both refresh on navigation and mutations.
    api<Badges>('/api/admin/badges').then(next => {
      if (generation === badgeRequest.current) setBadges(previous => ({ ...next, drives: previous?.drives }));
    }).catch(() => {});
    api<{ count: number }>('/api/admin/drives/untagged').then(next => {
      if (generation === badgeRequest.current) setBadges(previous => ({
        leads: 0, projects: 0, tasks: 0, finance: 0, outreach: 0, ...previous, drives: next.count,
      }));
    }).catch(() => {});
  }, []);
  useEffect(() => () => { badgeRequest.current++; }, []);

  // Refresh counts on every route change — cheap single query, keeps the
  // sidebar honest after edits without a realtime channel. Navigation also
  // closes the More sheet (its rows navigate, and so does the back button).
  useEffect(() => {
    setMoreOpen(false);
    loadBadges();
  }, [pathname, loadBadges]);

  // Capture before page-owned scroll containers disappear on navigation.
  // Keep the event's pathname with the offset, even if the frame runs later.
  useEffect(() => {
    let frame = 0;
    const pending = new Map<string, number>();
    const flush = () => {
      frame = 0;
      for (const [path, scroll] of pending) writeNavScroll(getSessionStorage(), path, scroll);
      pending.clear();
    };
    const onScroll = (event: Event) => {
      const el = event.target as HTMLElement | null;
      const path = currentPath.current;
      if (path === '/tools' || path.startsWith('/tools/') || !el?.classList?.contains('page-body')) return;
      pending.set(path, el.scrollTop);
      if (!frame) frame = requestAnimationFrame(flush);
    };
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      cancelAnimationFrame(frame);
      // Do not recreate a cleared cache when logout unmounts Shell.
      pending.clear();
    };
  }, []);

  // Data arrives after the skeleton. Restore once the new container can
  // actually hold the remembered offset, on desktop as well as phone.
  useEffect(() => {
    const pathChanged = previousRestorePath.current !== pathname;
    previousRestorePath.current = pathname;
    if (pathname === '/tools' || pathname.startsWith('/tools/')) return;
    if (pathChanged && !shouldRestoreScroll(navigationType, pathname)) {
      clearNavScroll(getSessionStorage(), pathname);
      const el = document.querySelector<HTMLElement>('.page-body');
      if (el) el.scrollTop = 0;
      return;
    }
    const saved = readNav(getSessionStorage(), pathname)?.scroll;
    if (!saved) return;
    let frame = 0;
    const startedAt = Date.now();
    const restore = () => {
      const el = document.querySelector<HTMLElement>('.page-body');
      if (el && el.scrollHeight - el.clientHeight >= saved) {
        el.scrollTop = saved;
        return;
      }
      if (Date.now() - startedAt < 3000) frame = requestAnimationFrame(restore);
    };
    frame = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(frame);
  }, [pathname, isMobile]);

  // The FAB gets out of the way of the software keyboard — the visual
  // viewport shrinks well below the layout height while the keys are up.
  useEffect(() => {
    if (!isMobile) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setKeyboardUp(vv.height < 0.7 * window.innerHeight);
    update();
    vv.addEventListener('resize', update);
    return () => vv.removeEventListener('resize', update);
  }, [isMobile]);

  // …and immediately after any mutation (Phase 1.11), so completing a task on
  // the Tasks page drops the sidebar count without a navigation. Coalesced so
  // a bulk action's burst of mutations only triggers one refetch.
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    const onMutated = () => {
      if (t) clearTimeout(t);
      t = setTimeout(loadBadges, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener('ss:mutated', onMutated);
    };
  }, [loadBadges]);

  useEffect(() => {
    const openSearch = () => setPalette('search');
    window.addEventListener('ss:open-search', openSearch);
    return () => window.removeEventListener('ss:open-search', openSearch);
  }, []);

  // ⌘K / Ctrl+K opens the palette as search; ⌘E / Ctrl+E straight into
  // new-task entry (phase 17 — ⌘T is browser-reserved, Henry picked E).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === 'k') {
        e.preventDefault();
        setPalette((p) => (p ? null : 'search'));
      } else if (key === 'e') {
        e.preventDefault();
        setPalette('task');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Keep the badge honest when the theme changes elsewhere — the Settings
  // Seg, or the OS while preference is System (applyTheme emits ss:theme).
  useEffect(() => {
    const onTheme = () => setTheme(readThemeState());
    window.addEventListener('ss:theme', onTheme);
    return () => window.removeEventListener('ss:theme', onTheme);
  }, []);

  const toggleTheme = () => {
    const preference = currentTheme();
    const systemPrefersLight = window.matchMedia('(prefers-color-scheme: light)').matches;
    const next = nextTheme(preference, systemPrefersLight);
    applyTheme(next);
    setTheme({ preference: next, effective: next });
  };

  const themeLabel =
    theme.preference === 'system' ? 'Auto' : theme.effective === 'dark' ? 'Dark' : 'Light';

  // The More sheet's Theme row cycles Auto → Dark → Light → Auto (§0.4.3);
  // the desktop sidebar toggle keeps its own dark/light flip.
  const cycleTheme = () => {
    const next: Theme =
      theme.preference === 'system' ? 'dark' : theme.preference === 'dark' ? 'light' : 'system';
    applyTheme(next);
    setTheme(readThemeState());
  };

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="workspace">
          <div className="logo-mark">S</div>
          <div>
            <div className="workspace-name">Sheahan OS</div>
            <span className="workspace-sub">Studio operations</span>
          </div>
        </div>

        <a className="nav-item" onClick={() => setPalette('search')} style={{ cursor: 'pointer' }}>
          <svg
            className="nav-icon"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
          >
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5L14 14" />
          </svg>
          <span className="nav-label">Search</span>
          <span className="nav-kbd">⌘K</span>
        </a>
        <NavItem to="/overview" label="Overview" icon={OverviewIcon} />

        {/* A group whose items all moved elsewhere renders nothing at all —
            no orphan heading. */}
        {SIDEBAR_GROUPS.map(group => (
          <Fragment key={group}>
            {renderSidebarGroup(SIDEBAR_GROUP_LABELS[group], sidebarLayout[group])}
          </Fragment>
        ))}

        <div className="sidebar-footer">
          <NavItem to="/automations" label="Automations" icon={AutomationsIcon} />
          <button type="button" className="nav-item" onClick={toggleTheme}>
            {theme.effective === 'dark' ? (
              <MoonIcon className="nav-icon" />
            ) : (
              <SunIcon className="nav-icon" />
            )}
            <span className="nav-label">Theme</span>
            <span className="nav-mode">
              {theme.preference === 'system'
                ? 'Auto'
                : theme.effective === 'dark'
                  ? 'Dark'
                  : 'Light'}
            </span>
          </button>
          <NavItem to="/settings" label="Settings" icon={SettingsIcon} />
        </div>
      </aside>

      {/* A context provider renders no DOM, so the bar and the FAB stay
          direct flex children of .app. */}
      <QuickAddProvider>
        <main className="main">{children}</main>
        {isMobile && (
          <TabBar badges={badges} moreOpen={moreOpen} onMore={() => setMoreOpen(true)} />
        )}
        {isMobile && !keyboardUp && !hidesFab(pathname) && (
          <button
            type="button"
            className="fab"
            aria-label="Quick add"
            onClick={() => setQuickAddOpen(true)}
          >
            <PlusIcon />
          </button>
        )}
        {isMobile && (
          <MoreSheet
            open={moreOpen}
            onClose={() => setMoreOpen(false)}
            badges={badges}
            themeLabel={themeLabel}
            onToggleTheme={cycleTheme}
            onSearch={() => {
              setMoreOpen(false);
              setPalette('search');
            }}
          />
        )}
        {/* Kept mounted: a row tap closes the sheet and leaves its form
            standing, so the form can't live inside the sheet. */}
        {isMobile && (
          <QuickAddSheet open={quickAddOpen} onClose={() => setQuickAddOpen(false)} />
        )}
      </QuickAddProvider>
      <CommandPalette open={palette !== null} task={palette === 'task'} onClose={() => setPalette(null)} />
    </div>
  );
}
